import React, { useState } from 'react';
import { DeveloperProjectSection, IDeveloperProject } from 'utils/entities';
import { useLocale } from 'locale';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { SectionWrapper, SubsectionWrapper } from 'screens/DeveloperPage/styled';
import ExpandableSection from './expandable-section';
import Card from './card';
import * as Styled from './styled';


const MAX_CARDS_DESKTOP = 8;
const MAX_CARDS_TAB = 6;


interface ProjectSectionProps {
  section: DeveloperProjectSection;
  projects: IDeveloperProject[];
  hasFilters?: boolean;
  isFirst?: boolean;
  isExpanded?: boolean;
  headingTagH3?: boolean;
}

export const ProjectSection: React.FC<ProjectSectionProps> = ({
  section,
  projects,
  hasFilters,
  isExpanded,
  isFirst,
  headingTagH3,
}) => {
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isTablet = screenBreakpoint === 2;
  const isDesktop = screenBreakpoint > 2;
  const maxCards = isTablet ? MAX_CARDS_TAB : MAX_CARDS_DESKTOP;
  const [ showMore, setShowMore ] = useState(true);
  if (!projects || !projects.length) return null;
  const isPromoted = section === DeveloperProjectSection.Promoted;

  const showMoreProjects = () => setShowMore(false);
  const maybeShowMore = !isPromoted && isDesktop && showMore && projects.length > maxCards;
  const displayProjects = maybeShowMore ? projects.slice(0, maxCards) : projects;

  const list = displayProjects.map(project => <Card project={project} key={project.id} headingTagH3={headingTagH3} />);
  const pagerList = (
    <Styled.ProjectSectionInner isPromoted={isPromoted} isScrollable>
      <ScrollBox autoWidth>
        {list}
      </ScrollBox>
    </Styled.ProjectSectionInner>
  );
  const inlineList = (
    <Styled.ProjectSectionInner>
      {list}
    </Styled.ProjectSectionInner>
  );

  if (isPromoted) {
    return (
      <Styled.ProjectPromotedSectionWrapper>
        <SectionWrapper noTopMargin>
          <SubsectionWrapper noTopMargin>
            <ShowAt at={1}>
              {pagerList}
            </ShowAt>
            <ShowAt from={2}>
              {inlineList}
            </ShowAt>
          </SubsectionWrapper>
        </SectionWrapper>
      </Styled.ProjectPromotedSectionWrapper>
    );
  }

  const title = t('developerPage.projects.sectionTitle', { section });

  const sectionHeader = (
    <Styled.ProjectSectionHeader weight="bold">{title} <Styled.ProjectCount>· {projects.length}</Styled.ProjectCount></Styled.ProjectSectionHeader>
  );

  return (
    <Styled.ProjectsWrapper>
      <SectionWrapper noTopMargin>
        <SubsectionWrapper noTopMargin>
          <ShowAt to={2}>
            <ExpandableSection title={title} expanded={(hasFilters && !isDesktop) || isExpanded} isFirst={isFirst} count={projects.length}>
              {pagerList}
            </ExpandableSection>
          </ShowAt>
          <ShowAt from={3}>
            {isFirst ? null : <Styled.Line />}
            {sectionHeader}
            {inlineList}
            {maybeShowMore ? (
              <Styled.ShowMoreProjects onClick={showMoreProjects}>
                {t('developerPage.projects.showMore', { section })}
              </Styled.ShowMoreProjects>
            ) : null}
          </ShowAt>
        </SubsectionWrapper>
      </SectionWrapper>
    </Styled.ProjectsWrapper>
  );
};
